
import { useState } from "react";
import { AssessmentView } from "./AssessmentView";
import { useAssessment } from "@/contexts/AssessmentContext";
import { useAssessmentResults } from "@/hooks/use-assessment-results";
import { useSaveAssessment } from "@/hooks/use-save-assessment";
import { useConnectionStatus } from "@/hooks/use-connection-status";
import { OrganizationDetailsStep } from "./OrganizationDetailsStep";
import { ResultsStep } from "./ResultsStep";

export const AssessmentRouter = () => {
  const [savedAssessmentId, setSavedAssessmentId] = useState<string | null>(null);
  
  // Check connection status
  useConnectionStatus();
  
  const { 
    organizationDetails, 
    handleOrganizationSubmit,
    currentQuestionIndex,
    handleAnswer,
    getCurrentAnswer,
    handleNext,
    handlePrevious,
    handleSkipToResults,
    showResults,
    answers,
    clearSession
  } = useAssessment();

  // Calculate assessment results when needed
  const assessmentResults = useAssessmentResults(answers, showResults);

  // Initialize the save assessment hook
  const { isSaving, saveAssessment } = useSaveAssessment({
    organizationDetails,
    answers,
    assessmentResults
  });

  const handleSaveAssessment = async () => {
    console.log("Triggering save assessment with simplified approach");
    const result = await saveAssessment();
    if (result.success && result.assessmentId) {
      setSavedAssessmentId(result.assessmentId);
    }
  };

  const handleStartNew = () => {
    clearSession();
    setSavedAssessmentId(null);
  };

  // Different views based on assessment state
  if (!organizationDetails) {
    return <OrganizationDetailsStep onSubmit={handleOrganizationSubmit} />;
  }

  if (showResults && assessmentResults) {
    return (
      <ResultsStep
        maturityLevel={assessmentResults.report.maturityLevel}
        overallScore={assessmentResults.overallScore}
        description={assessmentResults.report.description}
        dimensionScores={assessmentResults.dimensionScoresRecord}
        organizationDetails={organizationDetails}
        answers={answers}
        onStartNew={handleStartNew}
        onSave={handleSaveAssessment}
        isSaving={isSaving}
        savedAssessmentId={savedAssessmentId}
        assessmentId={savedAssessmentId || 'pending'}
      />
    );
  }

  return (
    <AssessmentView
      currentQuestionIndex={currentQuestionIndex}
      currentAnswer={getCurrentAnswer()}
      onAnswer={handleAnswer}
      onNext={handleNext}
      onPrevious={handlePrevious}
      onSkip={handleSkipToResults}
    />
  );
};
